import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Badge } from "./ui/badge";
import { getCurrencySymbol, cn } from "../lib/utils";
import { Clock, Wallet, BadgePoundSterling, Calendar } from "lucide-react";

const EmployeeDetailsPopover = ({ 
  employee, 
  currency = "GBP",
  children,
  side = "right",
  align = "start"
}) => {
  const [open, setOpen] = useState(false);
  
  if (!employee) return children;

  const currencySymbol = getCurrencySymbol(currency);
  const isSalary = employee.pay_type === "salary";

  const getSalaryPeriodLabel = (period) => {
    switch (period) {
      case "weekly": return "per week";
      case "monthly": return "per month";
      case "annual": return "per year";
      default: return "";
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={() => setOpen(true)}>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        side={side} 
        align={align} 
        className="w-72 p-0"
        data-testid={`employee-popover-${employee.id}`}
      >
        {/* Header with avatar and name */}
        <div className="p-4 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            {employee.avatar_url ? (
              <img 
                src={employee.avatar_url} 
                alt={employee.name}
                className="w-12 h-12 rounded-full object-cover border border-border"
              />
            ) : (
              <div 
                className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold",
                  !employee.color?.startsWith('#') && `avatar-${employee.color}`
                )}
                style={employee.color?.startsWith('#') ? { backgroundColor: employee.color + '20', color: employee.color } : {}}
              >
                {employee.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h4 className="font-semibold text-base">{employee.name}</h4>
              <p className="text-sm text-muted-foreground">{employee.role}</p>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="p-4 space-y-4">
          {/* Pay Type Badge */}
          <div className="flex items-center gap-2">
            <Badge variant={isSalary ? "secondary" : "outline"}>
              {isSalary ? "Salary" : "Hourly"}
            </Badge>
          </div>

          {/* Pay Information */}
          <div className="space-y-3">
            {isSalary ? (
              /* Salary Employee */
              <div className="flex items-start gap-3">
                <Wallet className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Salary</p>
                  <p className="text-sm text-muted-foreground">
                    {currencySymbol}{employee.salary_amount?.toLocaleString() || 0} {getSalaryPeriodLabel(employee.salary_period)}
                  </p>
                </div>
              </div>
            ) : (
              /* Hourly Employee */
              <>
                <div className="flex items-start gap-3">
                  <BadgePoundSterling className="w-4 h-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Base Rate</p>
                    <p className="text-sm text-muted-foreground">
                      {currencySymbol}{employee.hourly_rate?.toFixed(2) || "0.00"}/hr
                    </p>
                  </div>
                </div>

                {/* Additional Rates */}
                {employee.additional_rates && employee.additional_rates.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Wallet className="w-4 h-4 mt-0.5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Additional Rates</p>
                      <div className="mt-1 space-y-1">
                        {employee.additional_rates.map((rate, index) => (
                          <div key={index} className="flex justify-between text-sm text-muted-foreground">
                            <span>{rate.name}</span>
                            <span>{currencySymbol}{rate.rate?.toFixed(2)}/hr</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Contract Hours */}
            {employee.contracted_hours > 0 && (
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Contracted Hours</p>
                  <p className="text-sm text-muted-foreground">
                    {employee.contracted_hours}h per week
                  </p>
                </div>
              </div>
            )}

            {/* Minimum Hours */}
            {employee.minimum_hours > 0 && (
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Minimum Hours</p>
                  <p className="text-sm text-muted-foreground">
                    {employee.minimum_hours}h per week
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EmployeeDetailsPopover;
